import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { enqueueSnackbar } from 'notistack';

import Spinner from '@/components/dataDisplay/Spinner';
import Button from '@/components/input/Button';
import Input from '@/components/input/Input';
import Textarea from '@/components/input/Textarea';
import { routes } from '@/constants/routes';
import useRouteStages from '@/context/stages/hook';
import { stagesService } from '@/services/stages';

interface Props {
  routeId: string;
  stageId?: string;
  onSubmit?: () => void;
  onCancel?: () => void;
}

const CreateEditStageForm: React.FC<Props> = ({ stageId, routeId, onCancel, onSubmit }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { getStage, updateStage, addStage } = useRouteStages();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const handleCancel: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    if (onCancel) {
      onCancel();
    } else {
      navigate(generatePath(routes.admin.routes.detail, { routeId }));
    }
  };

  const handleReset = () => {
    setTitle('');
    setDescription('');
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const submitter = (e.nativeEvent as Event & { submitter: HTMLElement }).submitter as HTMLButtonElement;

    const form = e.currentTarget;
    const formData = new FormData();

    formData.append('title', title);
    formData.append('description', description);
    if (!stageId) {
      formData.append('routeId', routeId);
    }

    const service = () =>
      stageId ? stagesService.updateStage(stageId, formData) : stagesService.createStage(formData);

    service()
      .then((stage) => {
        if (stageId) {
          updateStage(routeId, stage);
        } else {
          addStage(routeId, stage);
        }
        if (onSubmit) {
          onSubmit();
        } else if (submitter.name === 'save') {
          navigate(generatePath(routes.admin.routes.detail, { routeId }));
        } else if (submitter.name === 'saveAndAddAnother') {
          if (!stageId) {
            handleReset();
            form.reset();
          }
        }
        const toasterMessage = routeId ? 'Etapa actualizada correctamente' : 'Etapa creada correctamente';
        enqueueSnackbar(toasterMessage, {
          variant: 'success',
        });
      })
      .catch((error) => {
        enqueueSnackbar('Error al crear o editar la etapa', {
          variant: 'error',
        });
        console.error('Error creating or editing route', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (routeId && stageId) {
      const stage = getStage(routeId, stageId);
      if (stage) {
        setTitle(stage.title);
        setDescription(stage.description);
      }
    }
  }, [getStage, routeId, stageId]);

  return (
    <form
      style={{ display: 'flex', flexDirection: 'column', gap: 16, overflowX: 'visible' }}
      onSubmit={handleSubmit}
    >
      <Input
        label="Título"
        name="title"
        required
        placeholder="Título"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <Textarea
        label="Descripción"
        name="description"
        placeholder="Descripción"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <div
        style={{
          display: 'flex',
          gap: 16,
          justifyContent: 'space-between',
          ...(loading && { opacity: 0.5 }),
        }}
      >
        <Button type="button" variant="danger" onClick={handleCancel} disabled={loading}>
          Cancelar
        </Button>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          {!routeId && (
            <Button type="submit" name="saveAndAddAnother" variant="outline" disabled={loading}>
              {stageId ? 'Guardar y seguir editando' : 'Guardar y añadir otro'}
            </Button>
          )}
          <Button type="submit" name="save" disabled={loading}>
            {loading && <Spinner style={{ marginRight: 8 }} />}
            Guardar
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateEditStageForm;
